import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { Modal } from '@/components/ui/Modal'
import { Toggle } from '@/components/ui/Toggle'
import { fetchAdminCategories, createCategory, updateCategory, deleteCategory } from '@/lib/api'
import type { Category } from '@/types'

const EMPTY: Partial<Category> = {
  name: '', description: '', selection_type: 'multiple', is_required: 0, sort_order: 99, is_active: 1,
}

function CategoryForm({
  init, onSave, onClose,
}: { init: Partial<Category>; onSave: (c: Category) => void; onClose: () => void }) {
  const [form, setForm] = useState<Partial<Category>>(init)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isNew = !form.id

  function set<K extends keyof Category>(key: K, val: Category[K]) {
    setForm((f) => ({ ...f, [key]: val }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const saved = isNew
        ? await createCategory(form)
        : await updateCategory(form.id!, form)
      onSave(saved)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
        <input
          type="text"
          value={form.name ?? ''}
          onChange={(e) => set('name', e.target.value)}
          required
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
        <textarea
          rows={2}
          value={form.description ?? ''}
          onChange={(e) => set('description', e.target.value)}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-y"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Selection Type</label>
          <select
            value={form.selection_type ?? 'multiple'}
            onChange={(e) => set('selection_type', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            <option value="multiple">Multiple</option>
            <option value="single">Single</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Sort Order</label>
          <input
            type="number"
            value={form.sort_order ?? 99}
            onChange={(e) => set('sort_order', Number(e.target.value))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Toggle
          checked={!!form.is_required}
          onChange={(v) => set('is_required', v ? 1 : 0)}
          label="Required"
        />
        {!isNew && (
          <Toggle
            checked={!!form.is_active}
            onChange={(v) => set('is_active', v ? 1 : 0)}
            label="Active"
          />
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : isNew ? 'Create Category' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

export default function AdminCategories() {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<'add' | Category | null>(null)

  useEffect(() => {
    fetchAdminCategories()
      .then(setCategories)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  function handleSaved(c: Category) {
    setCategories((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id)
      return idx >= 0 ? prev.map((x) => x.id === c.id ? c : x) : [...prev, c]
    })
    setModal(null)
  }

  async function handleDelete(c: Category) {
    if (!confirm(`Delete "${c.name}"? Options in this category will be orphaned.`)) return
    try {
      await deleteCategory(c.id)
      setCategories((prev) => prev.filter((x) => x.id !== c.id))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  return (
    <AdminLayout>
      <PageHeader
        title="Categories"
        subtitle="Manage option categories shown in the configurator"
        action={
          <button
            onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Category
          </button>
        }
      />

      {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden sm:table-cell">Type</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden md:table-cell">Order</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-5 py-3 font-medium text-slate-800">{cat.name}</td>
                  <td className="px-5 py-3 text-slate-500 hidden sm:table-cell capitalize">{cat.selection_type}</td>
                  <td className="px-5 py-3 text-slate-500 hidden md:table-cell">{cat.sort_order}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {cat.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setModal(cat)} className="text-slate-400 hover:text-brand-600 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(cat)} className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal
          title={modal === 'add' ? 'Add Category' : `Edit — ${(modal as Category).name}`}
          onClose={() => setModal(null)}
        >
          <CategoryForm
            init={modal === 'add' ? EMPTY : (modal as Category)}
            onSave={handleSaved}
            onClose={() => setModal(null)}
          />
        </Modal>
      )}
    </AdminLayout>
  )
}
